import React, { useState } from 'react';
import MySkinLogo from './MySkinLogo';

const Header = ({ hideHeader = false }) => {
  const [showAbout, setShowAbout] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (hideHeader) return null;

  const navLinks = [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Developers', href: '/developers' },
    { label: 'Playground', href: '/playground' },
    { label: 'API Docs', href: '/api-docs' },
  ];

  return (
    <>
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="mx-4 sm:mx-6 mt-4">
          <div className="bg-black/20 backdrop-blur-xl border border-white/10 rounded-2xl px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <button
                onClick={() => setShowAbout(true)}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <img src="/assets/LOGO.svg" alt="my-skin.ai" className="w-8 h-8 object-cover" />
                <span className="text-white font-semibold text-sm hidden sm:inline">my-skin.ai</span>
              </button>

              {/* Desktop Nav Links */}
              <div className="hidden md:flex items-center gap-1">
                <button
                  onClick={() => setShowAbout(true)}
                  className="px-3 py-1.5 text-white/70 hover:text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-all"
                >
                  About
                </button>
                {navLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="px-3 py-1.5 text-white/70 hover:text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-all"
                  >
                    {link.label}
                  </a>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              >
                {mobileMenuOpen ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden mt-3 pt-3 border-t border-white/10 space-y-1">
                <button
                  onClick={() => { setShowAbout(true); setMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2.5 text-white/80 hover:text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-all"
                >
                  About
                </button>
                {navLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block px-3 py-2.5 text-white/80 hover:text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-all"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in-scale">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 relative animate-fade-in-scale max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowAbout(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="mb-6">
              <MySkinLogo size="lg" showText={true} />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">About my-skin.ai</h2>
            <div className="space-y-4 text-gray-600">
              <p className="leading-relaxed font-medium text-sm">
                <strong className="text-blue-600">my-skin.ai</strong> helps you understand your skin better with instant AI-powered analysis. Get professional-grade insights about your skin in seconds, completely free.
              </p>
              <p className="leading-relaxed text-sm">
                Connect with trusted dermatologists near you instantly if any concerns are detected.
              </p>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-4 border border-red-100">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-red-900 text-sm">Your Photos Are Never Saved</p>
                      <p className="text-red-700 text-xs">Images are deleted immediately after analysis - zero storage, guaranteed</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-orange-900 text-sm">100% Private & Secure</p>
                      <p className="text-orange-700 text-xs">Your privacy is our promise - no tracking, no storage, no compromise</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4 border border-blue-100">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-blue-900 text-sm">AI-Powered Technology</p>
                      <p className="text-blue-700 text-xs">Advanced dermatological analysis in seconds</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-purple-900 text-sm">Connect with Experts</p>
                      <p className="text-purple-700 text-xs">Find trusted dermatologists near you instantly</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-100">
                <h3 className="font-semibold text-emerald-900 text-sm mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Powered By
                </h3>
                <p className="text-emerald-700 text-xs leading-relaxed">
                  Built with advanced AI technology by <strong>my-skin.ai</strong> - Empowering healthier skin for everyone.
                </p>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowAbout(false)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 px-8 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 hover:shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
